import { useEffect } from "react";

// Third Party
import { useQueryClient } from "@tanstack/react-query";

// Context
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Subscriptions
import { updateReconciliationSubscription } from "@/DataPages/api/inventory/reconciliation/subscriptions";

// Types
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { IPaginatedQueryCached } from "@/Common/api/types/pagination";
import { IReconciliation } from "@/DataPages/types/inventory/models/reconciliation";
import { IUpdateReconciliationResponse } from "../types/reconciliation";

interface IReconciliationSubscriptions {
	locationId: string;
	commissionCycleId?: string;
	payPeriodId?: string;
}

const useReconciliationSubscriptions = (props: IReconciliationSubscriptions) => {
	const { locationId, commissionCycleId, payPeriodId } = props;
	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();
	const queryClient = useQueryClient();
	const reconciliationSubscriptions = getSubscription(["updateReconciliation", locationId, commissionCycleId, payPeriodId]);

	reconciliationSubscriptions?.subscriptions?.onUpdateReconciliation?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateReconciliation", IUpdateReconciliationResponse>) => {
			const response = result.data?.onUpdateReconciliation;
			const {
				reconciliation,
				locationId: resultLocationId,
				commissionCycleId: resultCommissionCycleId,
				payPeriodId: resultPayPeriodId,
			} = response ?? {};
			if (!reconciliation || !resultLocationId || !resultCommissionCycleId || !resultPayPeriodId) {
				return;
			}
			const updateEdge = (edge: { node: IReconciliation; cursor: string }) => {
				if (edge.node._id !== reconciliation._id) {
					return edge;
				}
				return {
					...edge,
					node: reconciliation,
				};
			};
			queryClient.setQueryData(
				["reconciliation", resultLocationId, resultCommissionCycleId, resultPayPeriodId],
				(oldData: IPaginatedQueryCached) => {
					if (!oldData) {
						return oldData;
					}

					const updatedPages = oldData.pages.map((page) => {
						return {
							...page,
							edges: page.edges.map(updateEdge),
						};
					});

					return {
						...oldData,
						pages: updatedPages,
					};
				}
			);
		}
	);

	useEffect(() => {
		if (locationId && commissionCycleId && payPeriodId) {
			addSubscription(
				["updateReconciliation", locationId, commissionCycleId, payPeriodId],
				[updateReconciliationSubscription(locationId, commissionCycleId, payPeriodId)]
			);
		}
		return () => {
			deleteSubscription(["updateReconciliation", locationId, commissionCycleId, payPeriodId]);
		};
	}, [locationId, commissionCycleId, payPeriodId]);
};

export default useReconciliationSubscriptions;
